import { useCallback, useRef, useState } from 'react'
import UploadCloud from 'lucide-react/dist/esm/icons/upload-cloud'

import { cn } from '@/lib/utils'
import { DOCUMENT_DROP_ZONE_HEIGHT } from '@/components/dashboard/dashboardLayout'

/** Accepted upload types, kept in step with the backend allow-list (PDF, images, spreadsheets, Word). */
export const ACCEPTED_FILE_TYPES =
  '.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.xlsx,.xls,.csv,.docx,.doc'

interface FileDropZoneProps {
  /** Stable id so the whole zone acts as a label for the hidden file input. */
  inputId: string
  onFilesSelected: (files: File[]) => void
  disabled?: boolean
}

export function FileDropZone({ inputId, onFilesSelected, disabled = false }: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return
      onFilesSelected(Array.from(fileList))
    },
    [onFilesSelected],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault()
      setIsDragging(false)
      if (disabled) return
      handleFiles(event.dataTransfer.files)
    },
    [disabled, handleFiles],
  )

  return (
    <label
      htmlFor={inputId}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setIsDragging(true)
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/70 bg-muted/20 px-4 py-5 text-center transition-colors hover:border-primary/50 hover:bg-primary/[0.03]',
        DOCUMENT_DROP_ZONE_HEIGHT,
        isDragging && 'border-primary bg-primary/5',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
        <UploadCloud className="h-5 w-5 text-primary" />
      </span>
      <span className="text-sm font-medium text-foreground">
        <span className="text-primary">Tap to upload</span> or drag &amp; drop
      </span>
      <span className="text-xs text-muted-foreground">PDF, image, spreadsheet, or Word · up to 25MB</span>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        disabled={disabled}
        className="sr-only"
        onChange={(event) => {
          handleFiles(event.target.files)
          // Reset so selecting the same file again re-triggers change.
          event.target.value = ''
        }}
      />
    </label>
  )
}
